import IdentityButton from '../components/IdentityButton';
import { Search } from '@mui/icons-material';
import KaiTokenAmount from './KaiTokenAmount';
import { useCallback, useMemo, useState } from 'react';
import { useDebounceCallback } from 'usehooks-ts';
import { useGetIdentities } from '../hooks/useGetIdentities';

type Props = {
  owner?: string;
  externalFilter?: string;
  hideSearch?: boolean;
};

export function IdentityList({
  owner,
  externalFilter,
  hideSearch = false,
}: Props) {
  const [filter, setFilter] = useState('');
  const [debouncedFilter, setDebouncedFilter] = useState('');

  const debounceFilterCallback = useCallback((value: string) => {
    setDebouncedFilter(value);
  }, []);

  const debounceFilter = useDebounceCallback(debounceFilterCallback, 500);

  // Use external filter if provided, otherwise use internal filter
  const activeFilter =
    externalFilter !== undefined ? externalFilter : debouncedFilter;

  const {
    data,
    isLoading,
    isError,
    isPlaceholderData,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
  } = useGetIdentities({ filter: activeFilter, owner });

  const handleFilterChange = (value: string) => {
    setFilter(value);
    debounceFilter(value);
  };

  const identities = useMemo(() => data?.pages?.flat(), [data]);

  const sortedIdentities = useMemo(() => {
    if (!identities) {
      return identities;
    }

    return [...identities].filter(
      (identity): identity is NonNullable<typeof identity> => Boolean(identity),
    );
  }, [identities]);

  return (
    <div className="flex flex-col gap-4">
      {/* Search Input */}
      {!hideSearch && (
        <div className="relative max-w-md mx-auto w-full">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-text-tertiary" />
          <input
            type="search"
            value={filter}
            onChange={(e) => handleFilterChange(e.target.value)}
            placeholder="Search identity name..."
            className="w-full bg-bg-dark border border-divider rounded-xl py-3 pl-10 pr-4 text-text-default placeholder-text-placeholder text-sm focus:outline-none focus:ring-1 focus:ring-brand/50 focus:border-brand/50 transition-all"
          />
        </div>
      )}

      {/* Content */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-6 h-6 border-2 border-brand border-t-transparent rounded-full animate-spin" />
        </div>
      ) : isError ? (
        <div className="flex items-center justify-center py-12 text-text-tertiary text-sm">
          Error occurred when fetching identity list
        </div>
      ) : !sortedIdentities || sortedIdentities.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 gap-2">
          <Search className="w-8 h-8 text-text-tertiary/50" />
          <p className="text-text-tertiary text-sm">No results found.</p>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {/* Table */}
          <div
            className={`relative transition-opacity ${
              isPlaceholderData ? 'opacity-30' : 'opacity-100'
            }`}
          >
            {/* Header */}
            <div className="grid grid-cols-12 gap-4 px-4 py-3 border-b border-divider bg-bg-tertiary/30 rounded-t-xl">
              <div className="col-span-1 text-xs font-medium text-text-tertiary uppercase tracking-wider">
                Rank
              </div>
              <div className="col-span-7 text-xs font-medium text-text-tertiary uppercase tracking-wider">
                Identity
              </div>
              <div className="col-span-4 text-xs font-medium text-text-tertiary uppercase tracking-wider text-right">
                Staked KAI
              </div>
            </div>

            {/* Rows */}
            <div className="divide-y divide-divider">
              {sortedIdentities?.map((identity, index) =>
                identity ? (
                  <div
                    key={identity.identityRaw}
                    className="grid grid-cols-12 gap-4 px-4 py-3 hover:bg-bg-tertiary/30 transition-colors"
                  >
                    <div className="col-span-1 flex items-center">
                      <span
                        className={`text-sm font-medium ${
                          index === 0
                            ? 'text-yellow-400'
                            : index === 1
                              ? 'text-gray-300'
                              : index === 2
                                ? 'text-amber-600'
                                : 'text-text-secondary'
                        }`}
                      >
                        #{index + 1}
                      </span>
                    </div>
                    <div className="col-span-7 flex items-center">
                      <IdentityButton identityName={identity.identityRaw} />
                    </div>
                    <div className="col-span-4 flex items-center justify-end">
                      <KaiTokenAmount amount={BigInt(identity.stakedTokens)} />
                    </div>
                  </div>
                ) : null,
              )}
            </div>
          </div>

          {/* Loading overlay */}
          {isPlaceholderData && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-6 h-6 border-2 border-brand border-t-transparent rounded-full animate-spin" />
            </div>
          )}

          {/* Load More Button */}
          {hasNextPage && (
            <div className="flex justify-center pt-2">
              <button
                type="button"
                onClick={() => fetchNextPage()}
                disabled={!hasNextPage || isFetchingNextPage}
                className="px-6 py-2.5 text-sm font-medium rounded-xl border border-divider bg-bg-tertiary/50 text-text-secondary hover:bg-bg-tertiary hover:text-text-default transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isFetchingNextPage
                  ? 'Loading more...'
                  : hasNextPage
                    ? 'Load More'
                    : 'Nothing more to load'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default IdentityList;
